import Container from '@ui/container/container';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import { GetEventsResponse } from '@/types/projects.types';
import { useNavigate } from 'react-router-dom';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

type SaveTemplateConfirmationModalProps = {
  template: GetEventsResponse | null;
  isOpen: boolean;
  isPublished: boolean;
  onClose: () => void;
};

const SaveTemplateConfirmationModal = ({
  template,
  isOpen,
  isPublished,
  onClose,
}: SaveTemplateConfirmationModalProps) => {
  const navigate = useNavigate();

  return (
    <Modal
      open={isOpen}
      customSize={{
        width: '440px',
        height: 'auto',
        maxHeight: '260px',
        minHeight: '200px',
        maxWidth: 'calc(100vw - 80px)',
      }}
      contentWrapperClassName="padding-v-24 padding-h-40"
      backgroundColor="var(--gray-900)"
      RenderHeaderContent={() => (
        <Container>
          <div className="flex jc-space-between ai-center margin-b-8">
            <Text tag="h4" color="var(--gray-100)" className="H4">
              Success!
            </Text>
            {isPublished && (
              <button className="clear-button padding-4" onClick={onClose}>
                <Icon name={ICONS.CLOSE} size={16} color="var(--gray-400)" />
              </button>
            )}
          </div>
          <Text tag="p" color="var(--gray-400)" className="P2">
            Your project was saved as a template under:
          </Text>
        </Container>
      )}
      RenderFooterContent={() => (
        <Container className="flex fd-row jc-flex-end ai-center">
          {isPublished && (
            <Button
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
              className="L1 padding-v-8 padding-h-16 margin-r-8 height-48"
              onClick={onClose}
            >
              Back to Editing
            </Button>
          )}
          <Button
            width="135px"
            height="48px"
            className="flex ai-center padding-v-8 padding-h-16"
            hoverOpacity={0.8}
            onClick={() => {
              onClose();
              if (template?.uuid) {
                navigate(`/choose/preview-template/${template?.uuid}`, { replace: true });
              }
            }}
            backgroundColor={getThemeColorCSSVariable()}
          >
            <Text
              className="L1"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              View Template
            </Text>
          </Button>
        </Container>
      )}
    >
      <Container className="margin-v-16 overflow-auto flex-column gap-16">
        <Container background="var(--gray-700)" borderRadius="12px" height="64px" className="flex ai-center">
          <Text clamp={1} className="H4 padding-16 flex-1 text-ellipsis">
            {template?.name || 'Untitled Template'}
          </Text>
        </Container>
      </Container>
    </Modal>
  );
};

export default SaveTemplateConfirmationModal;
